'use client'

import { useState, useEffect, useRef } from 'react'

// Componente de Select com Busca
export interface SearchableSelectProps<T> {
  options: T[]
  value: string
  onChange: (value: string) => void
  getOptionValue: (option: T) => string
  getOptionLabel: (option: T) => string
  placeholder: string
  disabled?: boolean
  loading?: boolean
  emptyMessage?: string
  onNoResults?: (data: { searchTerm: string; field: string; currentValue: string; allFilters: Record<string, string> }) => void
  fieldName?: string
  allFilters?: Record<string, string>
  allowClear?: boolean
}

export function SearchableSelect<T>({
  options,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  placeholder,
  disabled = false,
  loading = false,
  emptyMessage = 'Nenhuma opção encontrada',
  onNoResults,
  fieldName = '',
  allFilters = {},
  allowClear = false
}: SearchableSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousSearchTermRef = useRef<string>('')

  // Fechar quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
        previousSearchTermRef.current = ''
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filtrar opções baseado no termo de busca
  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option)
    return label && label.toLowerCase().includes(searchTerm.toLowerCase())
  })

  // Disparar evento quando não houver resultados e houver termo de busca
  useEffect(() => {
    if (
      isOpen &&
      searchTerm.length > 0 &&
      filteredOptions.length === 0 &&
      options.length > 0 &&
      searchTerm !== previousSearchTermRef.current &&
      onNoResults &&
      fieldName
    ) {
      // Só disparar se realmente não encontrou resultados (não é lista vazia inicial)
      previousSearchTermRef.current = searchTerm
      onNoResults({
        searchTerm,
        field: fieldName,
        currentValue: value,
        allFilters
      })
    }
  }, [searchTerm, filteredOptions.length, isOpen, options.length, onNoResults, fieldName, value, allFilters])

  // Obter label do valor selecionado
  const selectedOption = options.find(opt => getOptionValue(opt) === value)
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : ''

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Input que simula o select */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={`
          w-full px-4 py-3 border border-gray-300 rounded-lg 
          focus:ring-2 focus:ring-green-500 focus:border-green-500 
          bg-white text-gray-900 text-left
          disabled:bg-gray-100 disabled:cursor-not-allowed
          flex items-center justify-between
        `}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {loading ? 'Carregando...' : (value ? displayValue : placeholder)}
        </span>
        <div className="flex items-center gap-2">
          {allowClear && value && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onChange('')
                setIsOpen(false)
                setSearchTerm('')
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Limpar seleção"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && !disabled && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Input de busca */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-white text-gray-900"
                autoFocus
              />
              <svg
                className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Lista de opções */}
          <div className="overflow-y-auto max-h-48">
            {/* Opção "Todos" quando allowClear está ativo */}
            {allowClear && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`
                  w-full px-4 py-2 text-left text-sm hover:bg-green-50 transition-colors border-b border-gray-200
                  ${!value ? 'bg-green-100 text-green-900 font-medium' : 'text-gray-900'}
                `}
              >
                Todos
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option)
                const optionLabel = getOptionLabel(option)
                const isSelected = value === optionValue

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(optionValue)}
                    className={`
                      w-full px-4 py-2 text-left text-sm hover:bg-green-50 transition-colors
                      ${isSelected ? 'bg-green-100 text-green-900 font-medium' : 'text-gray-900'}
                    `}
                  >
                    {optionLabel}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}


